import React, { useRef, useState } from 'react';
import { FileTextIcon, UploadCloudIcon, XIcon } from 'lucide-react';
type CvUploadProps = {
  file: File | null;
  error?: string;
  onChange: (file: File | null, error?: string) => void;
};
const MAX_BYTES = 5 * 1024 * 1024;
export function CvUpload({
  file,
  error,
  onChange
}: CvUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const accept = (f: File | undefined) => {
    if (!f) return;
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      onChange(null, 'Upload your CV as a PDF');
      return;
    }
    if (f.size > MAX_BYTES) {
      onChange(null, 'Your file is larger than 5 MB');
      return;
    }
    onChange(f);
  };
  if (file) {
    return <div className="flex items-center gap-3 rounded-[10px] border border-ink/15 bg-white px-4 py-3" data-id="element-223" data-mp="1jhwdrve3oqww">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] bg-brand-soft text-brand" data-id="element-224" data-mp="1erpqbyvbrcg8">
          <FileTextIcon className="h-5 w-5" aria-hidden="true" data-id="element-225" data-mp="2z9pvk4lum4gi" />
        </span>
        <span className="min-w-0 flex-1" data-id="element-226" data-mp="348sztmu086nf">
          <span className="block truncate text-[15px] font-medium text-ink" data-id="element-227" data-mp="347rg6tyxabu3">{file.name}</span>
          <span className="block text-[13px] text-ink-muted" data-id="element-228" data-mp="2pjde4l9byrel">{(file.size / 1024 / 1024).toFixed(1)} MB · PDF</span>
        </span>
        <button type="button" onClick={() => onChange(null)} aria-label="Remove CV" className="flex h-10 w-10 items-center justify-center rounded-full text-ink-muted transition-colors duration-150 ease-out hover:bg-canvas-warm hover:text-ink" data-id="element-229" data-mp="2uh6fn0zoux8j">
          <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-230" data-mp="26n56jasui4g4" />
        </button>
      </div>;
  }
  return <div onDragOver={e => {
    e.preventDefault();
    setDragging(true);
  }} onDragLeave={() => setDragging(false)} onDrop={e => {
    e.preventDefault();
    setDragging(false);
    accept(e.dataTransfer.files[0]);
  }} className={`flex flex-col items-center rounded-[12px] border-2 border-dashed px-6 py-8 text-center transition-colors duration-150 ease-out ${dragging ? 'border-brand bg-brand-soft' : error ? 'border-[#C0362C]' : 'border-ink/15 bg-white'}`} data-id="element-231" data-mp="2bmhqnnsc0xf0">
      <UploadCloudIcon className="h-7 w-7 text-brand" aria-hidden="true" data-id="element-232" data-mp="3pki4fup66z2u" />
      <p className="mt-3 text-[15px] text-ink" data-id="element-233" data-mp="r2cx3at6yxnh">
        <span className="hidden sm:inline" data-id="element-234" data-mp="r11h8cmh1zg0">Drag and drop your CV here, or </span>
        <button type="button" id="apply-cv" onClick={() => inputRef.current?.click()} aria-describedby="apply-cv-help" className="font-semibold text-brand underline-offset-2 hover:underline" data-id="element-235" data-mp="m2zy5rp1q04j">
          <span className="sm:hidden" data-id="element-236" data-mp="cbdqg5bx2j6j">Choose a file</span>
          <span className="hidden sm:inline" data-id="element-237" data-mp="19tvxd9vtzxlt">browse</span>
        </button>
      </p>
      <p id="apply-cv-help" className="mt-1 text-[13px] text-ink-muted" data-id="element-238" data-mp="151m3xjvicfes">
        PDF, max 5 MB
      </p>
      <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="sr-only" tabIndex={-1} onChange={e => {
      accept(e.target.files?.[0]);
      e.target.value = '';
    }} data-id="element-239" data-mp="1dbjpl5krsvuv" />
    </div>;
}